import React, {CSSProperties, MouseEventHandler} from 'react';
import axios from 'axiosWrapper';

/**
 * Proxy endpoint for "Resizer" service.
 */
const RESIZER_ENDPOINT = '/api/resizer';

/**
 * Options of "Resizer" service.
 * @see https://wiki.yandex-team.ru/mds/resizer/#ispolzovanieresajjzera
 */
export interface ResizedImageOptions {
    url?: string | null;
    width?: number;
    height?: number;
    crop?: boolean;
    enlarge?: string;
    proxy?: string;
    typemap?: string;
    uid?: string;
    format?: string;
    noautoorient?: string;
    quality?: number;
}

/**
 * Set resized image as background-image CSS rule for element.
 * @param {ResizedImageOptions} options
 * @param {HTMLElement} node
 * @returns {void}
 */
export function setResizedBackgroundImage(
    options: ResizedImageOptions,
    node: HTMLElement | null,
): void {
    if (!node) {
        return;
    }

    const imageSrc = getResizedImageUrl({
        width: node?.clientWidth,
        height: node?.clientHeight,
        crop: true,
        ...options,
    });
    node.style.backgroundImage = `url(${imageSrc})`;
}

/**
 * Generates Uri for resized image.
 * @param {ResizedImageOptions} options
 * @param {number} ratio
 * @returns {string}
 */
export function getResizedImageUrl(options: ResizedImageOptions, ratio = 1) {
    if (!options.url || (!options.width && !options.height)) {
        return options.url;
    }

    if (!options.width || !options.height) {
        options.crop = false;
    }

    const width = options.width || 0;
    const height = options.height || 0;
    const params = {
        ...options,
        crop: options.crop ? 'yes' : 'no',
        width: width * ratio,
        height: height * ratio,
    };

    return axios.getUri({url: RESIZER_ENDPOINT, params});
}

export interface ResizedImageProps {
    options: ResizedImageOptions;
    className?: string;
    alt?: string;
    style?: CSSProperties;
    onClick?: MouseEventHandler;
}

export default class ResizedImage extends React.Component<ResizedImageProps> {
    render() {
        const {alt, className, options, style, onClick} = this.props;
        return (
            <img
                alt={alt}
                className={className}
                ref={(node: HTMLImageElement) => this.initResizedImage(options, node)}
                onClick={onClick}
                style={style}
            />
        );
    }

    private initResizedImage(options: ResizedImageOptions, node: HTMLImageElement) {
        if (!node) {
            return;
        }

        const localOptions: ResizedImageOptions = {
            width: node.clientWidth,
            height: node.clientHeight,
            crop: true,
            ...options,
        };

        const imageSrc = getResizedImageUrl(localOptions);

        node.src = imageSrc ?? '';
        node.srcset = `
            ${imageSrc} 1x,
            ${getResizedImageUrl(localOptions, 2)} 2x,
            ${getResizedImageUrl(localOptions, 3)} 3x,
        `;
    }
}
